import { useState, useCallback, useEffect, useMemo } from "react";
import { usePreload } from "../DataContext/PreloadContext";
import { fetchClasses } from "../../services/classService";
import { buildQueryString } from "../../helpers/buildQueryString";
import {
  mergePipeStrings,
  flattenSelectedProperties,
} from "../../helpers/EntityHelper";
import { includedFeaturePageCache } from "../../pages/cache/PageCache";
import { saveIncludedFeatures } from "../../services/includedFeatureService";

export default function usePageIncludedFeature({ id, language, deps = [] }) {
  const cacheKey = `${id}_${language}`;
  const { showNotification, confirmDialog, withLoader } = usePreload();

  const [searchTerm, setSearchTerm] = useState("");
  // starts true so the grid shows skeletons, not the empty state, before the first load
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedClass, setSelectedClass] = useState(
    location.state?.selectedClass || null,
  );

  const [currentIncludedFeature, setCurrentIncludedFeature] = useState({
    selectedIncludedFeatures: [],
  });

  const loadData = useCallback(
    async (force = false) => {
      if (!force) {
        const cached = includedFeaturePageCache.get(cacheKey);
        if (cached) {
          setSelectedClass(cached);
          setIsLoading(false);
          return;
        }
      }

      setIsLoading(true);
      setLoadError(false);
      try {
        await withLoader(async () => {
          try {
            const classResponse = await fetchClasses(
              buildQueryString({
                searchOn: "classCode",
                language: language,
                page: 0,
                rowsPerPage: 1,
                searchTerm: id,
              }),
            );
            const foundClass =
              classResponse.total > 0
                ? classResponse.results[0]
                : selectedClass;

            // don't cache a miss: updateAllCache reads every entry's includedFeatures
            if (foundClass) includedFeaturePageCache.set(cacheKey, foundClass);
            setSelectedClass(foundClass);
          } catch (e) {
            // withLoader swallows errors; remember it so the page can tell a
            // failed load from a class that doesn't exist
            setLoadError(true);
            throw e;
          }
        });
      } finally {
        setIsLoading(false);
      }
    },
    [id, language, searchTerm, withLoader],
  );

  useEffect(() => {
    loadData();
  }, deps);

  useEffect(() => {
    if (!selectedClass) return;

    const included = selectedClass?.includedFeatures ?? [];
    const includedFlattened = included.map((m) => {
      const flattened = flattenSelectedProperties(
        m,
        "feature",
        "featureId",
        m.includedFeatureFeatureId,
        [
          "featureDisplayName",
          "featureType",
          "featureIsActive",
          "featureImage",
          "featureSelectable",
        ],
      );

      return {
        ...flattened,
        // stable row key for the grid (new rows already use the same id)
        id: flattened.id ?? flattened.includedFeatureUniqueId,
        isDefault: true,
        //includedFeatureIsActive:
        //flattened.includedFeatureIsActive ??
        //flattened.includedFeatureIsactive,
        includedFeatureUnitCode: flattened.includedFeatureUnitCode
          ? flattened.includedFeatureUnitCode
          : mergePipeStrings(
              flattened.feature?.[0]?.defaultUnit?.map((v) => v.unitCode) ?? [],
              [],
            ),
        unit: flattened.includedFeatureUnitCode
          ? flattened.unit
          : (flattened.feature?.[0]?.defaultUnit?.map((m) => ({
              ...m,
              isDefault: true,
            })) ?? []),
        includedFeatureValues: mergePipeStrings(
          flattened.feature?.[0]?.defaultValues?.map((v) => v.valueCode) ?? [],
          flattened.includedFeatureValues ?? [],
        ),
        values: [
          ...(flattened.feature?.[0]?.defaultValues?.map((m) => ({
            ...m,
            isDefault: true,
          })) ?? []),
          ...flattened.values,
        ],
      };
    });

    // Only update local state if there is a difference
    setCurrentIncludedFeature((prev) => {
      return { ...prev, selectedIncludedFeatures: includedFlattened };
    });
  }, [selectedClass]);

  const handleFeatureChange = useCallback(
    (state) => {
      const includedFeatureUniqueId = `${id}_${state.item.id}`;
      const featureId = state.item.id;
      if (state.action === "add") {
        const alreadySelected =
          currentIncludedFeature?.selectedIncludedFeatures?.some(
            (f) => f.includedFeatureFeatureId === featureId,
          );

        const maxIndex = Math.max(
          0,
          ...(currentIncludedFeature?.selectedIncludedFeatures || [])
            .map((f) => Number(f?.includedFeatureIndexOrder))
            .filter((n) => !isNaN(n)),
        );

        if (alreadySelected) {
          showNotification("Feature was already included!", "warning");
          return;
        }

        const featureType = state.item.featureType ?? "";

        const newSelected = {
          id: includedFeatureUniqueId,
          includedFeatureUniqueId: includedFeatureUniqueId,
          featureDisplayName: state.item.featureDisplayName ?? "",
          featureType: featureType,
          featureIsActive: state.item.featureIsActive,
          includedFeatureType: featureType,
          //includedFeatureIsActive: state.item.featureIsActive ?? true,
          includedFeatureClassCode: id,
          includedFeatureFeatureId: featureId,
          includedFeatureIndexOrder: `${maxIndex + 1}`,
          isSelectionNew: true,

          values:
            state.item.defaultValues?.map((v) => ({ ...v, isDefault: true })) ??
            [],
          unit:
            state.item.defaultUnit?.map((u) => ({ ...u, isDefault: true })) ??
            [],

          includedFeatureValues: mergePipeStrings(
            state.item.defaultValues?.map((v) => v.valueCode) ?? [],
          ),
          includedFeatureUnitCode: mergePipeStrings(
            state.item.defaultUnit?.map((u) => u.unitCode) ?? [],
          ),
        };

        upsertCache(newSelected);
      } else if (state.action === "delete") {
        removeFromCache(includedFeatureUniqueId);
      }
    },
    [id, showNotification, currentIncludedFeature.selectedIncludedFeatures],
  );

  const handleSearchClick = (value) => {
    setSearchTerm(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchClick(e.target.value);
    }
  };

  const filteredIncludedFeatures = useMemo(() => {
    const term = searchTerm?.toLowerCase() || "";

    return (
      currentIncludedFeature.selectedIncludedFeatures
        ?.filter(
          (f) =>
            f.includedFeatureFeatureId?.toLowerCase().includes(term) ||
            f.featureDisplayName?.toLowerCase().includes(term),
        )
        .sort(
          (a, b) =>
            Number(!!b.isSelectionNew) - Number(!!a.isSelectionNew) ||
            //Number(!!b.includedFeatureIsActive) - Number(!!a.includedFeatureIsActive) ||
            Number(!!b.featureIsActive) - Number(!!a.featureIsActive) ||
            Number(a.includedFeatureIndexOrder) -
              Number(b.includedFeatureIndexOrder),
        ) ?? []
    );
  }, [currentIncludedFeature?.selectedIncludedFeatures, searchTerm]);

  const upsertCache = (updatedRow) => {
    setCurrentIncludedFeature((prev) => ({
      ...prev,
      selectedIncludedFeatures: [
        ...prev.selectedIncludedFeatures.filter(
          (i) =>
            i.includedFeatureUniqueId !== updatedRow.includedFeatureUniqueId,
        ),
        updatedRow,
      ],
    }));

    updateAllCache((list) => {
      const exists = list.some(
        (i) => i.includedFeatureUniqueId === updatedRow.includedFeatureUniqueId,
      );

      return exists
        ? list.map((i) =>
            i.includedFeatureUniqueId === updatedRow.includedFeatureUniqueId
              ? { ...i, ...updatedRow }
              : i,
          )
        : [updatedRow, ...list];
    });
  };

  const removeFromCache = (id) => {
    setCurrentIncludedFeature((prev) => ({
      ...prev,
      selectedIncludedFeatures: prev.selectedIncludedFeatures.filter(
        (f) => f.includedFeatureUniqueId !== id,
      ),
    }));

    updateAllCache((list) =>
      list.filter((i) => i.includedFeatureUniqueId !== id),
    );
  };

  const updateAllCache = (callback) => {
    const entries = [...includedFeaturePageCache.cache];

    for (const [key, cached] of entries) {
      if (!cached) continue;
      includedFeaturePageCache.set(key, {
        ...cached,
        includedFeatures: callback(cached.includedFeatures ?? []),
      });
    }
  };

  const handleSaveNewFeatures = async () => {
    const ok = await confirmDialog.show({
      title: "Included Features",
      message: "Are you sure you want to save changes on these features?",
    });

    if (!ok) return;

    try {
      confirmDialog.setLoading(true);

      const features = currentIncludedFeature?.selectedIncludedFeatures || [];

      const includedFeaturesToSave = features.filter(
        (feature) => feature.isSelectionNew,
      );

      if (includedFeaturesToSave.length === 0) {
        showNotification("No new features to save.", "info");
        return;
      }

      const response = await saveIncludedFeatures(includedFeaturesToSave);
      if (response instanceof Response && !response.ok) {
        throw new Error(`Save failed (${response.status})`);
      }

      const updatedFeatures = features.map((feature) =>
        feature.isSelectionNew
          ? { ...feature, isSelectionNew: false }
          : feature,
      );

      setCurrentIncludedFeature((prev) => ({
        ...prev,
        selectedIncludedFeatures: updatedFeatures,
      }));

      // upsertCache stored these rows as new; clear the flag there too so a
      // revisit doesn't show them as unsaved and re-post them
      const savedIds = new Set(
        includedFeaturesToSave.map((f) => f.includedFeatureUniqueId),
      );
      updateAllCache((list) =>
        list.map((i) =>
          savedIds.has(i.includedFeatureUniqueId)
            ? { ...i, isSelectionNew: false }
            : i,
        ),
      );

      showNotification("Feature/s successfully added!", "success");
    } catch (e) {
      console.error("Failed to save included features:", e);
      showNotification(`ERROR: ${e}`, "error");
    } finally {
      confirmDialog.close();
    }
  };

  return {
    currentIncludedFeature,
    filteredIncludedFeatures,
    handleFeatureChange,
    reload: loadData,
    selectedClass,
    searchTerm,
    isLoading,
    loadError,
    handleKeyDown,
    handleSearchClick,
    upsertCache,
    removeFromCache,
    handleSaveNewFeatures,
  };
}
